import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Input } from './Input';
import { Button } from './Button';
import SafeIcon from '../../common/SafeIcon';
import FflDirectoryLookup, { directoryRecordToContact } from './FflDirectoryLookup';
import { getFflStatus } from '../../lib/fflStatus';
import { FiChevronDown, FiPlus, FiX, FiSearch } from 'react-icons/fi';

const FFL_STATUS_TEXT_CLASSES = {
  expired: 'text-red-600 dark:text-red-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  ok: 'text-green-600 dark:text-green-400',
};

const EMPTY_CONTACT = {
  type: 'DEALER',
  name: '',
  contact_name: '',
  ffl_number: '',
  ffl_expiration_date: '',
  phone: '',
  email: '',
  address: '',
  ffl_directory_number: null,
  ffl_last_verified_at: null
};

const RESULT_LIMIT = 50;

function formatLabel(c) {
  if (!c) return '';
  if (c.type === 'DEALER') {
    return c.contact_name ? `${c.name} (${c.contact_name})` : c.name;
  }
  return `${c.name} (Customer)`;
}

export default function ContactSelect({ label, value, onChange, onSelectContact, required, placeholder, defaults }) {
  const { tenant } = useAuth();

  // Search / dropdown state
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);

  // Selected contact's full record (so we can show its label in the input).
  const [selectedContact, setSelectedContact] = useState(null);

  // Refs for outside-click + scroll-into-view
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Create-modal state (unchanged from prior version)
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);

  // Load the selected contact's full record when `value` changes from outside
  // (e.g. pre-filled from a pending order's guessed_customer_id).
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setSelectedContact(null);
      return;
    }
    if (selectedContact?.id === value) return;
    api.getContactById(value)
      .then((c) => { if (!cancelled) setSelectedContact(c || null); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced server-side search whenever the dropdown is open.
  useEffect(() => {
    if (!open || !tenant) return;
    let cancelled = false;
    const delay = search ? 250 : 0;
    setLoadingResults(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.searchContacts(tenant.id, { search, limit: RESULT_LIMIT });
        if (!cancelled) setResults(data);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoadingResults(false);
      }
    }, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, search, tenant]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-option]');
    if (items[highlightIdx]) items[highlightIdx].scrollIntoView({ block: 'nearest' });
  }, [highlightIdx]);

  // Reset highlight as the user types
  useEffect(() => { setHighlightIdx(-1); }, [search]);

  const selectedLabel = formatLabel(selectedContact);

  const selectContact = (c) => {
    setSelectedContact(c);
    onChange(c.id);
    onSelectContact?.(c);
    setOpen(false);
    setSearch('');
    setHighlightIdx(-1);
    inputRef.current?.blur();
  };

  const clearSelection = () => {
    setSelectedContact(null);
    onChange('');
    onSelectContact?.(null);
    setSearch('');
    inputRef.current?.focus();
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        setHighlightIdx(0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIdx >= 0 && results[highlightIdx]) {
          selectContact(results[highlightIdx]);
        } else if (results.length === 1) {
          selectContact(results[0]);
        }
        break;
      case 'Tab':
        if (highlightIdx >= 0 && results[highlightIdx]) {
          selectContact(results[highlightIdx]);
        } else {
          setOpen(false);
          setSearch('');
          setHighlightIdx(-1);
        }
        break;
      case 'Escape':
        setOpen(false);
        setSearch('');
        setHighlightIdx(-1);
        break;
      default:
        break;
    }
  };

  const openCreateModal = () => {
    setNewContact({ ...EMPTY_CONTACT, ...(defaults || {}) });
    setOpen(false);
    setSearch('');
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!newContact.name.trim()) return;
    if (!tenant) {
      alert('No tenant associated with your account.');
      return;
    }
    setSaving(true);
    try {
      const contactData = {
        type: newContact.type,
        name: newContact.name.trim(),
        phone: newContact.phone.trim() || null,
        email: newContact.email.trim() || null,
        address: newContact.address.trim() || 'N/A'
      };
      if (newContact.type === 'DEALER') {
        contactData.contact_name = newContact.contact_name.trim() || null;
        contactData.ffl_number = newContact.ffl_number.trim() || null;
        contactData.ffl_expiration_date = newContact.ffl_expiration_date || null;
        contactData.ffl_directory_number = newContact.ffl_directory_number || null;
        contactData.ffl_last_verified_at = newContact.ffl_last_verified_at || null;
      } else {
        contactData.contact_name = null;
        contactData.ffl_number = null;
        contactData.ffl_expiration_date = null;
        contactData.ffl_directory_number = null;
        contactData.ffl_last_verified_at = null;
      }
      const created = await api.createContact(tenant.id, contactData);
      setSelectedContact(created);
      onChange(created.id);
      onSelectContact?.(created);
      setShowModal(false);
      setNewContact(EMPTY_CONTACT);
    } catch (err) {
      console.error('Error creating contact:', err);
      alert(`Error creating contact: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const inputDisplayValue = open ? search : selectedLabel;
  const showEmptyState = !loadingResults && results.length === 0;

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>

      <div className="flex gap-1.5" ref={containerRef}>
        <div className="relative flex-1">
          <SafeIcon
            icon={FiSearch}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
          />
          <input
            ref={inputRef}
            type="text"
            value={inputDisplayValue}
            placeholder={placeholder || `Search ${(label || 'contact').toLowerCase()}...`}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
              setSearch('');
            }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-9 pr-16 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-900 dark:border-slate-700 dark:text-white"
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
            {value && !open && (
              <button
                type="button"
                tabIndex={-1}
                onClick={clearSelection}
                title="Clear selection"
                className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <SafeIcon icon={FiX} className="h-3.5 w-3.5" />
              </button>
            )}
            <SafeIcon
              icon={FiChevronDown}
              className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </div>

          {open && (
            <ul
              ref={listRef}
              className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:bg-slate-900 dark:border-slate-700"
            >
              {loadingResults && (
                <li className="px-3 py-2 text-slate-400">Searching…</li>
              )}
              {!loadingResults && showEmptyState && (
                <li className="px-3 py-2 text-slate-400">
                  {search ? `No matches for "${search}"` : 'No contacts yet — click + to add one'}
                </li>
              )}
              {!loadingResults && results.map((c, idx) => {
                const isSelected = c.id === value;
                const isHighlighted = idx === highlightIdx;
                const fflStatus = getFflStatus(c);
                const showStatusMessage = fflStatus && fflStatus.level !== 'ok';
                const detail = [c.ffl_number ? `FFL ${c.ffl_number}` : null, c.email, c.phone].filter(Boolean).join(' • ');
                return (
                  <li
                    key={c.id}
                    data-option
                    onClick={() => selectContact(c)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={`cursor-pointer px-3 py-2 ${
                      isHighlighted
                        ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300'
                        : isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/30 font-medium text-primary-700 dark:text-primary-400'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{formatLabel(c)}</div>
                        {(showStatusMessage || detail) && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {showStatusMessage && (
                              <span className={FFL_STATUS_TEXT_CLASSES[fflStatus.level]}>
                                {fflStatus.message}{detail ? ' • ' : ''}
                              </span>
                            )}
                            {detail}
                          </div>
                        )}
                      </div>
                      <span className={`text-[10px] font-semibold uppercase shrink-0 ${
                        fflStatus ? FFL_STATUS_TEXT_CLASSES[fflStatus.level] : 'text-slate-400'
                      }`}>
                        {c.type === 'DEALER' ? 'Dealer' : 'Customer'}
                      </span>
                    </div>
                  </li>
                );
              })}
              {!search && results.length === RESULT_LIMIT && (
                <li className="px-3 py-1.5 text-[11px] text-slate-400 italic border-t border-slate-100 dark:border-slate-700">
                  Showing {RESULT_LIMIT} most recent — type to search the rest.
                </li>
              )}
            </ul>
          )}

          {/* Hidden input so HTML required-validation works on submit */}
          <input
            type="text"
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
            required={!!required}
            value={value || ''}
            onChange={() => {}}
          />
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          title="Add new contact"
          className="flex items-center justify-center h-10 w-10 shrink-0 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 transition-colors dark:bg-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-primary-900/30 dark:hover:text-primary-400"
        >
          <SafeIcon icon={FiPlus} className="h-4 w-4" />
        </button>
      </div>

      {showModal && (
        <NewContactModal
          contact={newContact}
          setContact={setNewContact}
          onSave={handleCreate}
          onClose={() => setShowModal(false)}
          saving={saving}
        />
      )}
    </div>
  );
}

function NewContactModal({ contact, setContact, onSave, onClose, saving }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Add New Contact</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setContact({ ...contact, type: 'DEALER' })}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                contact.type === 'DEALER'
                  ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                  : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
              }`}
            >
              Dealer / FFL
            </button>
            <button
              type="button"
              onClick={() => setContact({ ...contact, type: 'CUSTOMER', ffl_number: '', contact_name: '' })}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                contact.type === 'CUSTOMER'
                  ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                  : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
              }`}
            >
              Customer
            </button>
          </div>
          <Input
            label={contact.type === 'DEALER' ? 'Business Name *' : 'Name *'}
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            placeholder={contact.type === 'DEALER' ? 'e.g. Liberty Arms LLC' : 'e.g. John Smith'}
            autoFocus
          />
          {contact.type === 'DEALER' && (
            <>
              <FflDirectoryLookup
                onSelect={(rec) => setContact({ ...contact, ...directoryRecordToContact(rec) })}
              />
              <Input
                label="FFL Number"
                value={contact.ffl_number}
                onChange={(e) => setContact({ ...contact, ffl_number: e.target.value })}
                placeholder="e.g. 1-23-456-78-9A-01234"
              />
              <Input
                label="FFL Expiration Date"
                type="date"
                value={contact.ffl_expiration_date}
                onChange={(e) => setContact({ ...contact, ffl_expiration_date: e.target.value })}
              />
              <Input
                label="Contact Name"
                value={contact.contact_name}
                onChange={(e) => setContact({ ...contact, contact_name: e.target.value })}
                placeholder="e.g. John Smith"
              />
            </>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Phone"
              type="tel"
              value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
            <Input
              label="Email"
              type="email"
              value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })}
              placeholder="contact@example.com"
            />
          </div>
          <Input
            label="Address"
            value={contact.address}
            onChange={(e) => setContact({ ...contact, address: e.target.value })}
            placeholder="123 Main St, City, State ZIP"
          />
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            onClick={onSave}
            isLoading={saving}
            disabled={!contact.name.trim()}
            icon={FiPlus}
          >
            Add Contact
          </Button>
        </div>
      </div>
    </div>
  );
}
